import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Form, Modal } from 'semantic-ui-react'
import { AppPrimaryButton, AppSecondaryButton, CustomDropdown } from 'elements'
import { AutoTicketService } from 'utility/services/auto-ticket'
import { ActionPlanRuleService } from 'utility'
import {
    normalizeAssignableUsers,
    normalizeLookupNameList,
    getApiErrorMessage,
} from './utils'

export const ReassignModal = ({
    open,
    onClose,
    ticketId,
    onSuccess,
    inline = false,
}) => {
    const [query, setQuery] = useState('')
    const [users, setUsers] = useState([])
    const [teams, setTeams] = useState([])
    const [selectedId, setSelectedId] = useState('')
    const [selectedTeamId, setSelectedTeamId] = useState('')
    const [note, setNote] = useState('')
    const [searching, setSearching] = useState(false)
    const [teamsLoading, setTeamsLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    const loadUsers = useCallback(async searchText => {
        setSearching(true)
        setError('')
        try {
            const params = { top: 20 }
            if (searchText?.trim()) params.q = searchText.trim()
            const res = await AutoTicketService.getAssignableUsers(params)
            setUsers(normalizeAssignableUsers(res))
        } catch (err) {
            setError(getApiErrorMessage(err))
            setUsers([])
        } finally {
            setSearching(false)
        }
    }, [])

    const loadTeams = useCallback(async () => {
        setTeamsLoading(true)
        try {
            let res
            try {
                res = await ActionPlanRuleService.getLookupTeams()
            } catch {
                res = await ActionPlanRuleService.getTeams()
            }
            setTeams(normalizeLookupNameList(res))
        } catch {
            setTeams([])
        } finally {
            setTeamsLoading(false)
        }
    }, [])

    useEffect(() => {
        if (!open) return undefined
        setQuery('')
        setSelectedId('')
        setSelectedTeamId('')
        setNote('')
        setError('')
        loadUsers('')
        loadTeams()
        return undefined
    }, [open, loadUsers, loadTeams])

    useEffect(() => {
        if (!open) return undefined
        const timer = setTimeout(() => loadUsers(query), 300)
        return () => clearTimeout(timer)
    }, [query, open, loadUsers])

    const handleSubmit = async () => {
        const assignedToTypeId = selectedTeamId ? 2 : 1
        const assignedToId = selectedTeamId || selectedId
        if (!assignedToId) {
            setError('Search a user or pick a team.')
            return
        }
        setSubmitting(true)
        setError('')
        try {
            await AutoTicketService.reassign(ticketId, {
                assignedToTypeId,
                assignedToId: Number(assignedToId),
                note: note.trim() || undefined,
            })
            onSuccess?.()
            onClose?.()
        } catch (err) {
            setError(getApiErrorMessage(err))
        } finally {
            setSubmitting(false)
        }
    }

    const userOptions = useMemo(
        () =>
            users.map(u => ({
                key: String(u.id),
                value: String(u.id),
                text: u.email ? `${u.name} (${u.email})` : u.name,
            })),
        [users],
    )

    const teamOptions = useMemo(
        () =>
            teams.map(team => ({
                key: String(team.id),
                value: String(team.id),
                text: team.name,
            })),
        [teams],
    )

    if (!open) return null

    const form = (
                <Form error={Boolean(error)}>
                    {error && (
                        <Form.Field>
                            <div style={{ color: '#b91c1c', fontSize: 13 }}>
                                {error}
                            </div>
                        </Form.Field>
                    )}
                    <Form.Field>
                        <label>Assign to user</label>
                        <CustomDropdown
                            placeholder={
                                searching
                                    ? 'Loading users…'
                                    : 'Search by name or email'
                            }
                            fluid
                            search
                            selection
                            clearable
                            selectOnBlur={false}
                            options={userOptions}
                            value={selectedId}
                            onSearchChange={(_, { searchQuery }) =>
                                setQuery(searchQuery || '')
                            }
                            onChange={(_, { value }) => {
                                setSelectedId(value ? String(value) : '')
                                if (value) setSelectedTeamId('')
                            }}
                            loading={searching}
                            disabled={searching && !userOptions.length}
                            noResultsMessage={
                                searching ? 'Searching…' : 'No users found'
                            }
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Or pick a team</label>
                        <CustomDropdown
                            placeholder={
                                teamsLoading ? 'Loading teams…' : 'Select team'
                            }
                            fluid
                            search
                            selection
                            clearable
                            selectOnBlur={false}
                            options={teamOptions}
                            value={selectedTeamId}
                            onChange={(_, { value }) => {
                                setSelectedTeamId(value ? String(value) : '')
                                if (value) setSelectedId('')
                            }}
                            loading={teamsLoading}
                            noResultsMessage={
                                teamsLoading ? 'Loading…' : 'No teams found'
                            }
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Note (optional)</label>
                        <Form.TextArea
                            placeholder="Reason for reassignment…"
                            value={note}
                            onChange={(_, { value }) => setNote(value)}
                            rows={2}
                        />
                    </Form.Field>
                </Form>
    )

    const actions = (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: inline ? 12 : 0,
            }}
        >
            <AppSecondaryButton onClick={onClose} disabled={submitting}>
                Cancel
            </AppSecondaryButton>
            <AppPrimaryButton
                onClick={handleSubmit}
                loading={submitting}
                disabled={submitting || (!selectedId && !selectedTeamId)}
            >
                Save
            </AppPrimaryButton>
        </div>
    )

    if (inline) {
        return (
            <div>
                <strong
                    style={{
                        display: 'block',
                        marginBottom: 10,
                        fontSize: 14,
                    }}
                >
                    Change owner
                </strong>
                {form}
                {actions}
            </div>
        )
    }

    return (
        <Modal open={open} onClose={onClose} size="small">
            <Modal.Header>Change owner</Modal.Header>
            <Modal.Content>{form}</Modal.Content>
            <Modal.Actions>{actions}</Modal.Actions>
        </Modal>
    )
}
